/**
 * Persists last explicit property selection per tenant + product scope (localStorage).
 * URL remains canonical when present; this hydrates when `property_id` is absent from the URL.
 */

import type { UserPropertiesFeatureScope } from '@/lib/property-select/user-properties-scope';

export const PROPERTY_SELECTION_URL_PARAM = 'property_id';

/** URL `property_id` values that are valid for the current scope allow-list (deduped). */
export function getScopeValidUrlPropertyIds(
  searchParams: URLSearchParams,
  allowIdSet: ReadonlySet<string>
): string[] {
  const raw = searchParams.getAll(PROPERTY_SELECTION_URL_PARAM).filter(Boolean);
  const uniq = [...new Set(raw)];
  return uniq.filter((id) => allowIdSet.has(id));
}

function normalizePropertyIdList(ids: readonly string[]): string {
  return [...new Set(ids.filter(Boolean))].sort().join(',');
}

/**
 * True when raw URL `property_id` values differ from `targetIds` (extra invalid ids,
 * duplicates, or a different set). Order is ignored.
 */
export function urlPropertyIdsNeedSanitization(
  searchParams: URLSearchParams,
  targetIds: readonly string[]
): boolean {
  const raw = searchParams.getAll(PROPERTY_SELECTION_URL_PARAM).filter(Boolean);
  const hasParam = searchParams.has(PROPERTY_SELECTION_URL_PARAM);

  if (targetIds.length === 0 && !hasParam) {
    return false;
  }

  const rawUniq = [...new Set(raw)];
  if (raw.length !== rawUniq.length) {
    return true;
  }

  return (
    normalizePropertyIdList(rawUniq) !== normalizePropertyIdList(targetIds)
  );
}

export const PROPERTY_SELECTION_STORAGE_KEY =
  'surface_property_id_selection_by_scope_v1';

export type PropertySelectionStorageSchema = {
  schemaVersion: 1;
  byTenant: Record<
    string,
    Partial<Record<UserPropertiesFeatureScope, string[]>>
  >;
};

function load(): PropertySelectionStorageSchema {
  if (typeof localStorage === 'undefined') {
    return { schemaVersion: 1, byTenant: {} };
  }
  const raw = localStorage.getItem(PROPERTY_SELECTION_STORAGE_KEY);
  if (!raw) return { schemaVersion: 1, byTenant: {} };
  try {
    const parsed = JSON.parse(raw) as PropertySelectionStorageSchema;
    if (parsed?.schemaVersion !== 1 || typeof parsed.byTenant !== 'object') {
      return { schemaVersion: 1, byTenant: {} };
    }
    return parsed;
  } catch {
    return { schemaVersion: 1, byTenant: {} };
  }
}

function save(data: PropertySelectionStorageSchema): void {
  if (typeof localStorage === 'undefined') return;
  try {
    localStorage.setItem(PROPERTY_SELECTION_STORAGE_KEY, JSON.stringify(data));
  } catch {
    // QuotaExceeded or private-mode restrictions must not abort callers
    // (selectProperties still needs to update the URL).
  }
}

/** Replace stored ids for scope (deduped). */
export function writeStoredIdsForScope(
  tenantId: string,
  scope: UserPropertiesFeatureScope,
  ids: string[]
): void {
  const data = load();
  if (!data.byTenant[tenantId]) data.byTenant[tenantId] = {};
  const uniq = [...new Set(ids.filter(Boolean))];
  if (uniq.length === 0) {
    delete data.byTenant[tenantId][scope];
    if (Object.keys(data.byTenant[tenantId]).length === 0) {
      delete data.byTenant[tenantId];
    }
  } else {
    data.byTenant[tenantId][scope] = uniq;
  }
  save(data);
}

export function readStoredIdsForScope(
  tenantId: string | undefined,
  scope: UserPropertiesFeatureScope
): string[] {
  if (!tenantId) return [];
  const row = load().byTenant[tenantId]?.[scope];
  return Array.isArray(row) ? row.filter(Boolean) : [];
}

export function clearAllPropertySelectionStorage(): void {
  if (typeof localStorage === 'undefined') return;
  localStorage.removeItem(PROPERTY_SELECTION_STORAGE_KEY);
}
